import { ActionIcon, Box, FileButton, Group, Image, Stack, Text, Textarea } from "@mantine/core";
import { useInputState } from "@mantine/hooks";
import { IconPhoto, IconPlayerPlay, IconTrash } from "@tabler/icons-react";
import type { JSX, KeyboardEvent, MouseEvent } from "react";
import { useMemo } from "react";
import { useImageSelect } from "../hooks/useImageSelect";
import classes from "./ChatInput.module.css";

interface IProps {
    askAi: (data: { content: string; imageFile?: File | null }) => Promise<void>;
    sending: boolean;
}

function ChatInput({ askAi, sending }: IProps): JSX.Element {
    const [value, handleChange] = useInputState("");
    const {
        value: imageFile,
        preview: imagePreview,
        name: imageName,
        onSelect,
        clear,
        error,
    } = useImageSelect<File>({
        toValue: async (file) => ({
            value: file,
            preview: URL.createObjectURL(file),
            name: file.name,
        }),
        validate: (file) => {
            const validTypes = ["image/png", "image/jpeg", "image/webp"];
            if (!validTypes.includes(file.type)) {
                return "Please upload a PNG, JPEG, or WebP image.";
            }
            return null;
        },
    });

    const hasText = useMemo(() => value.trim().length > 0, [value]);

    const canSend = useMemo(() => {
        return hasText && !sending;
    }, [hasText, sending]);

    const handleOnKeyDown = (event: KeyboardEvent) => {
        if (!canSend) {
            return;
        }
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    const handleSend = (_event?: MouseEvent<HTMLButtonElement>) => {
        if (!canSend) {
            return;
        }
        void askAi({ content: value, imageFile }).then(() => {
            handleChange("");
            clear();
        });
    };

    return (
        <Stack gap="xs">
            {imagePreview && (
                <Box className={classes.previewWrapper}>
                    <Image
                        src={imagePreview}
                        w={128}
                        h={128}
                        radius="md"
                        alt={imageName || "Selected image"}
                        className={classes.previewImage}
                    />
                    <ActionIcon variant="light" className={classes.trash} onClick={clear} aria-label="Remove image">
                        <IconTrash />
                    </ActionIcon>
                    <Text size="sm" className={classes.filename}>
                        {imageName}
                    </Text>
                    {error && (
                        <Text size="xs" c="red">
                            {error}
                        </Text>
                    )}
                </Box>
            )}
            <Group gap="xs" align="flex-end" className={classes.inputRow}>
                <FileButton accept="image/png,image/jpeg,image/webp" onChange={onSelect}>
                    {(props) => (
                        <ActionIcon {...props} size={36} variant="light" disabled={sending}>
                            <IconPhoto />
                        </ActionIcon>
                    )}
                </FileButton>
                <Textarea
                    className={classes.textarea}
                    placeholder="Ask the AI"
                    autosize
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleOnKeyDown}
                    disabled={sending}
                />
                <ActionIcon size={36} onClick={handleSend} disabled={!canSend}>
                    <IconPlayerPlay />
                </ActionIcon>
            </Group>
        </Stack>
    );
}

export default ChatInput;
